/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Injectable} from '@angular/core';

import {OAuthService} from 'angular-oauth2-oidc';
import {AppConfigService} from '../config/app-config.service';
import {VacationTrackerUser} from '../user/vacation-tracker-user';

@Injectable()
export class AuthenticationService {
  /** Observable which will return the user's information from the token */
  get userInfo(): VacationTrackerUser {
    if (this.appConfigService.getConfig().auth.enabled) {
      const claims = this.oAuthService.getIdentityClaims();
      return AuthenticationService.constructUserFromIdentityClaims(claims);
    } else {
      return AuthenticationService.constructMockUser();
    }
  }

  constructor(private readonly appConfigService: AppConfigService, private readonly oAuthService: OAuthService) {
  }

  async initialize(): Promise<boolean> {
    if (this.appConfigService.getConfig().auth.enabled) {
      this.oAuthService.configure(this.appConfigService.getConfig().auth);
      return this.oAuthService.loadDiscoveryDocumentAndLogin();
    } else {
      return Promise.resolve(true);
    }
  }

  getAccessToken(): any {
    if (this.appConfigService.getConfig().auth.enabled) {
      return this.oAuthService.getAccessToken();
    }
  }

  /**
   * Will setup up silent refreshing for when the token is about to expire.
   * When the user is logged out via this.logOut method, the silent refreshing will pause and not refresh the tokens
   * until the user is logged back in via receiving a new token.
   */
  enableSilentRenew(): void {
    if (this.appConfigService.getConfig().auth.enabled) {
      this.oAuthService.setupAutomaticSilentRefresh();
    }
  }

  /**
   * Removes all tokens and logs the user out. If a logout url is configured,
   * the user is redirected to it.
   */
  logout(): void {
    if (this.appConfigService.getConfig().auth.enabled) {
      this.oAuthService.logOut();
    }
  }

  /**
   * Creates a VLiveUser from the claims object passed in.
   *
   * @param claims Object consisting of the user's properties
   * @private
   */

  private static constructUserFromIdentityClaims(claims: any): VacationTrackerUser{
    let user: VacationTrackerUser = {};
    if (claims) {
      user = {
        gmDepartment: claims.gmDepartment ?? '',
        gmDepartmentDescription: claims.gmDepartmentDescription ?? '',
        gmLocationSiteName: claims.gmlocationsitename ?? '',
        gmin: claims.gmuniqueidentifier ?? '',
        firstName: claims.givenName ?? '',
        lastName: claims.sn ?? '',
        displayName: claims.displayName ?? '',
        email: claims.mail ?? '',
        roles: claims.roles ?? [],
      };
    }
    return user;
  }

  private static constructMockUser(): VacationTrackerUser {
    return {
      gmDepartment: 'IT_GMGCS_1001487',
      gmDepartmentDescription: 'LMK_ASD',
      gmLocationSiteName: 'GMIT Limerick Office',
      gmin: '123456789',
      firstName: 'John',
      lastName: 'Ireland',
      displayName: 'John Ireland',
      email: 'noreply@gm.com',
      roles: ['site_admin'],
    };

  }
}
